import { verifyOAuthTokenApi } from "@/api/services/main/auth";
import { getProductContractHandoffApi } from "@/api/services/main/product-contract";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { AppRoutes } from "@/routes/config";
import { setMatchAuthCookies } from "@/utils/auth-cookies";
import { showError } from "@/utils/error";
import { logout } from "@/utils/logout";
import { rememberCenterProductContractId } from "@/utils/oauth";
import { getSafeRedirectUrl } from "@/utils/url";
import { FC, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Resolves the staged contract and keeps its id for the next hand-off back to the center.
 * Best-effort: the id is only a hint (the center re-checks it against the caller's own grants),
 * so an expired or revoked contractToken must not fail an otherwise successful login.
 */
const rememberHandoffContract = async (contractToken: string | null) => {
  if (!contractToken) return;

  try {
    const rs = await getProductContractHandoffApi({ token: contractToken });
    rememberCenterProductContractId(rs.data.data?.productContractId);
  } catch {
    // Unresolvable contract -> the next hand-off just omits it and the center falls back to home.
  }
};

/**
 * OAuth handoff callback.
 *
 * A single-use `token` is minted server-side and exchanged here for a match
 * session via `verifyOAuthTokenApi`, then the user is dropped on the requested
 * page. Kept feature-agnostic so any future SSO/handoff flow can reuse it.
 */
export const AuthCallbackPage: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const redirectUrl = searchParams.get("redirectUrl");
  const contractToken = searchParams.get("contractToken");

  const verify = useCallback(async () => {
    // Clear any stale auth cookies before verifying so every navigate path starts clean.
    logout();

    if (!token) {
      navigate(AppRoutes.login, { replace: true });
      return;
    }

    try {
      const rs = await verifyOAuthTokenApi({ token });
      setMatchAuthCookies(rs.data);
      // After the cookies on purpose: the handoff endpoint is authenticated, so it can only
      // resolve the contract once this session is persisted.
      await rememberHandoffContract(contractToken);

      const target = getSafeRedirectUrl(redirectUrl ?? "");
      // A same-origin absolute URL is still a full page load for the router.
      if (target.startsWith("/")) {
        navigate(target, { replace: true });
      } else {
        window.location.replace(target);
      }
    } catch (err) {
      showError(err);
      navigate(AppRoutes.pageNotFound, { replace: true });
    }
  }, [token, redirectUrl, contractToken, navigate]);

  useEffect(() => {
    verify();
  }, [verify]);

  return <LoadingScreen />;
};
